import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';

/**
 * Rank 3 — in-product SOC triage simulator.
 *
 * The trainee works the live alert queue (useRangeAlerts) and classifies each
 * alert TP/FP via `classifyAlert` in services/score/handler.py. The backend
 * scores the decision through the existing FP/TP matrix and folds it into a
 * per-(tenant,range,user) running SOC score, read back via `getSocScore`.
 */

export type AlertClassification = 'tp' | 'fp';
export type TriageAction =
  | 'none'
  | 'dismiss'
  | 'escalate'
  | 'investigate'
  | 'isolate'
  | 'block';

export interface SocScoreSummary {
  range_id: number;
  user_id: number;
  soc_score: number;
  classified: number;
  correct_count: number;
  accuracy: number;
  tp_caught: number;
  fp_dismissed: number;
  misses: number;
  classifications: ReadonlyArray<{
    alert_id: string;
    event_id: string;
    classification: AlertClassification;
    action: TriageAction;
    is_real_attack: boolean;
    correct: boolean;
    delta: number;
    classified_at: string;
  }>;
}

export interface ClassifyAlertInput {
  rangeId: string;
  alertId: string;
  classification: AlertClassification;
  action?: TriageAction;
  /** Ground-truth key (sigma rule / decoy id). Defaults to alertId server-side. */
  eventId?: string;
}

export interface ClassifyAlertResult {
  range_id: number;
  alert_id: string;
  classification: AlertClassification;
  action: TriageAction;
  is_real_attack: boolean;
  correct: boolean;
  delta: number;
  rationale: string;
  soc_score: number;
  classified: number;
  correct_count: number;
  accuracy: number;
  tp_caught: number;
  fp_dismissed: number;
  misses: number;
}

export const socKeys = {
  score: (rangeId: string) => ['soc', 'score', rangeId] as const,
};

/** Running SOC triage score + per-alert history for one range. */
export function useSocScore(rangeId: string | undefined) {
  return useQuery<SocScoreSummary>({
    queryKey: socKeys.score(rangeId ?? ''),
    enabled: Boolean(rangeId),
    refetchInterval: 15_000,
    queryFn: async ({ signal }) =>
      apiFetch<SocScoreSummary>('/ranges/{rangeId}/soc-score', {
        method: 'GET',
        pathParams: { rangeId: rangeId ?? '' },
        signal,
      }),
  });
}

/** Classify a single alert TP/FP and re-pull the running score. */
export function useClassifyAlert() {
  const qc = useQueryClient();
  return useMutation<ClassifyAlertResult, Error, ClassifyAlertInput>({
    mutationFn: async ({ rangeId, alertId, classification, action, eventId }) =>
      apiFetch<ClassifyAlertResult>('/ranges/{rangeId}/alerts/classify', {
        method: 'POST',
        pathParams: { rangeId },
        body: {
          range_id: Number(rangeId),
          alert_id: alertId,
          classification,
          action: action ?? 'none',
          ...(eventId ? { event_id: eventId } : {}),
        },
      }),
    onSuccess: (_d, { rangeId }) => {
      qc.invalidateQueries({ queryKey: socKeys.score(rangeId) });
    },
  });
}
